import type { CascaderOption } from 'antd-mobile'
import type { AreaResponse } from '@/types/auth'
import i18n from '@/lang'
import { showToast } from '@/components/toast'

interface Options {
  label?: string | undefined
  value?: string | number | undefined
  disabled?: boolean | undefined
  children?: Options[] | undefined
}

// 获取url参数，返回参数值
export const getUrlParamValue = (paramName: string, url?: string): string => {
  const currentUrl = url || window.location.href

  const regex = new RegExp(`[?&]${paramName}=([^&#]*)`, 'i')
  const match = regex.exec(currentUrl)
  return match ? decodeURIComponent(match[1]) : ''
}

// 国家站点数据处理为andt-mobile的cascader数据格式
export const handleAreaData = (data: AreaResponse): CascaderOption[] => {
  return data.map((item) => {
    const option: CascaderOption = {
      label: item.name,
      value: item.id?.toString()
    }
    if (item.children && item.children.length > 0) {
      option.children = handleAreaData(item.children)
    }

    return option
  })
}

// 根据value获取label值
export const findLabelByValue = (options: Options[], value: string | number): string | null => {
  for (const option of options) {
    if (option.value === value) {
      return option?.label || null
    }
    if (option.children && option.children.length > 0) {
      const label = findLabelByValue(option.children, value)
      if (label) {
        return label
      }
    }
  }
  return null
}

// 文档上传前校验（PDF 或图片）
export const beforeDocumentUpload = (file: File, maxSize = 10) => {
  if (!file) return null
  const ext = file.name.split('.').pop()?.toLowerCase() || ''
  const isPdf = file.type === 'application/pdf' || ext === 'pdf'
  const isImage =
    file.type.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'].includes(ext) ||
    isHeicFile(file)

  if (!isPdf && !isImage) {
    showToast(i18n.t('tips.file.type.error'))
    return null
  }

  return beforeUpload(file, maxSize)
}

// 图片上传前校验
export const beforeUpload = (file: File, maxSize = 2) => {
  if (!file) return null
  const size = file?.size || 0
  const isLimit = size / 1024 / 1024 < maxSize

  if (!isLimit) {
    showToast(i18n.t('tips.picture.size.error', { maxSize }))

    return null
  }

  return file
}

export const isHeicFile = (file: File) => {
  const heicMimeTypes = ['image/heic', 'image/heic-sequence', 'image/heif', 'image/heif-sequence']
  const heicExtensions = ['.heic', '.heif']

  const fileName = file.name || ''
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || ''
  return heicMimeTypes.includes(file.type) || heicExtensions.includes(`.${fileExtension}`)
}
